import {of as observableOf, combineLatest as observableCombineLatest, Observable} from 'rxjs';

import {map, takeWhile} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone} from '@angular/core';
import {DetailControlOption, ValuesObject} from '../detail-tabconfig';
import {AbstractControlComponent} from './abstract-control';
import {ViewTabComponent} from '../viewtab.component';
import {optioncontrolRequired} from './validators/optioncontrol-required-validator';
import {ErrorService} from '../../../error/error.service';

@Component({
  selector: 'ctrl-simpleradiobuttons',
  template: `
    <mat-radio-group [formControl]="formControl">
      @for (opt of options; track opt) {
        <span>
          @if (getCaughtObservable(opt.Value) | async) {
            <mat-radio-button
              [value]="(getCaughtObservable(opt.Value) | async)?.Values"
              [disabled]="(getCaughtObservable(isEnabled) | async) != true"
            >
              {{ (getCaughtObservable(opt.Caption) | async)?.Values }}
            </mat-radio-button>
          }
        </span>
      }
    </mat-radio-group>
    @if (
      (getCaughtObservable(isEnabled) | async) &&
      formControl.touched &&
      formControl.hasError('required')
    ) {
      <mat-error>
        <language key="ControlErrorRequired" defaultValue="This field is required."></language>
      </mat-error>
    }
  `,
  styles: [
    `
      /* TODO(mdc-migration): The following rule targets internal classes of radio that may no longer apply for the MDC version. */
      mat-radio-button {
        margin-right: 20px;
      } /* TODO(mdc-migration): The following rule targets internal classes of radio that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-radio-label {
        align-items: center;
      } /* TODO(mdc-migration): The following rule targets internal classes of radio that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-radio-container {
        top: -1px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlSimpleRadiobuttonsComponent extends AbstractControlComponent {
  constructor(
    _parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
  ) {
    super(_parent, 'SelectedItem', 'SelectedItem', changeDetector, _ngZone, errorService);
  }

  @Input() objectType: string;

  protected initValidators(): void {
    if (this._config == null || this.formControl == null) {
      throw Error('Initialized target too early.');
    }

    this._config
      .getBooleanPropertyValues('Required')
      .pipe(takeWhile(() => this.alive))
      .subscribe(required => {
        if (required === true) {
          this.formControl.setValidators([optioncontrolRequired()]);
        } else {
          this.formControl.clearValidators();
        }
        this.formControl.updateValueAndValidity({emitEvent: false});
      });
    this.refreshOptions();
  }

  options: DetailControlOption[] = [];

  private refreshOptions() {
    if (this._config == null) {
      this.options = [];
      return;
    }
    let trueObs = this._config.getProperty('TrueText').pipe(
      map(valObj => {
        if (valObj == null || valObj.Values == null) return 'True';
        return valObj.Values;
      }),
    );
    let falseObs = this._config.getProperty('FalseText').pipe(
      map(valObj => {
        if (valObj == null || valObj.Values == null) return 'False';
        return valObj.Values;
      }),
    );
    observableCombineLatest(trueObs, falseObs, (trueText, falseText) => {
      //true value
      let cfgOpt: DetailControlOption = new DetailControlOption();
      cfgOpt.setCaption(observableOf(new ValuesObject(trueText)));
      cfgOpt.setValue(observableOf(new ValuesObject(true)));
      cfgOpt.setHint(observableOf(new ValuesObject(trueText)));
      this.options.push(cfgOpt);

      //false value
      cfgOpt = new DetailControlOption();
      cfgOpt.setCaption(observableOf(new ValuesObject(falseText)));
      cfgOpt.setValue(observableOf(new ValuesObject(false)));
      cfgOpt.setHint(observableOf(new ValuesObject(falseText)));
      this.options.push(cfgOpt);
      this.changeDetector.markForCheck();
    }).subscribe();
  }

  protected formatSource(source: any): any {
    if (source === '' || source == null) source = DetailControlOption.NullValueOverwrite;
    if (typeof source === 'boolean') {
      return source;
    } else if (typeof source === 'string') {
      let s = source.toLowerCase().trim();
      if (s === 'true') return true;
      if (s === 'false') return false;
      return null;
    }
    return source;
  }

  protected formatTarget(value: any): any {
    if (value === DetailControlOption.NullValueOverwrite) value = null;
    return value;
  }
}
